/* ============ COOKIE CONSENT ============
 * Real (if minimal) behavior for the cookie banner markup that
 * layout.mjs's cookieBannerMarkup() emits on every page (#cookie-banner,
 * hidden by default, with [data-cookie-accept]/[data-cookie-decline]
 * buttons inside it).
 *
 * - On first load (no stored choice), the banner is shown.
 * - Accept/decline stores { choice, timestamp } as JSON under the
 *   'dsvg-consent' localStorage key, hides the banner, and dispatches a
 *   'dsvg:consent-changed' CustomEvent on document.
 * - Any element on the page marked [data-cookie-reopen] (the footer link,
 *   the Contact page FAQ link) reopens the banner regardless of any prior
 *   stored choice.
 *
 * This site does not currently load any analytics, advertising or tracking
 * script — see /cookies/ for the honest, current footprint. This module is
 * forward-compatible infrastructure: if a future script needs to run only
 * after consent, it MUST gate its own initialization on either
 * localStorage.getItem('dsvg-consent') or the 'dsvg:consent-changed' event
 * below, rather than assuming consent or running unconditionally. No such
 * listener exists yet — none is needed until a script actually requires
 * consent.
 */

const STORAGE_KEY = 'dsvg-consent';

function readStoredConsent() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    // localStorage may be unavailable (privacy mode, storage disabled, full
    // quota) — treat as "no stored choice" rather than throwing.
    return null;
  }
}

function storeConsent(choice) {
  const record = { choice, timestamp: new Date().toISOString() };
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(record));
  } catch {
    // Consent just won't persist across reloads if storage isn't available;
    // the banner still hides for this page view.
  }
  return record;
}

function showBanner(banner) {
  banner.removeAttribute('hidden');
}

function hideBanner(banner) {
  banner.setAttribute('hidden', '');
}

function dispatchConsentChanged(record) {
  document.dispatchEvent(new CustomEvent('dsvg:consent-changed', { detail: record }));
}

/**
 * Reopens the cookie banner regardless of any prior stored choice. Exported
 * so a future page-specific script could call it directly; the default
 * wiring below also binds it to any [data-cookie-reopen] element.
 */
function openCookieBanner() {
  const banner = document.getElementById('cookie-banner');
  if (!banner) return; // No-op if the banner markup isn't present on this page.
  showBanner(banner);
}

function initCookieConsent() {
  const banner = document.getElementById('cookie-banner');
  if (!banner) return; // Guard: layout() should always include it, but never assume.

  const acceptBtn = banner.querySelector('[data-cookie-accept]');
  const declineBtn = banner.querySelector('[data-cookie-decline]');

  if (!readStoredConsent()) {
    showBanner(banner);
  }

  acceptBtn?.addEventListener('click', () => {
    const record = storeConsent('accepted');
    hideBanner(banner);
    dispatchConsentChanged(record);
  });

  declineBtn?.addEventListener('click', () => {
    const record = storeConsent('declined');
    hideBanner(banner);
    dispatchConsentChanged(record);
  });

  document.querySelectorAll('[data-cookie-reopen]').forEach((el) => {
    el.addEventListener('click', (event) => {
      event.preventDefault();
      openCookieBanner();
    });
  });
}

export { initCookieConsent, openCookieBanner };
